import Flex from '@Components/Bases/Flex';
import ModalRedirect from '@Components/Modal/ModalRedirect/ModalRedirect';
import {
  CVUP_ROLE,
  useGetUserProfile,
  useRefreshToken,
} from '@Domains/Profile';
import { useDisclosure } from '@hooks/useDisclosure';
import { CircularProgress } from '@mui/material';
import { ReactNode, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useProfile } from 'src/Constant/UserProfileContext/UserProfileProvider';

type ProtectRouteProps = {
  children: ReactNode;
};

const ProtectRoute = ({ children }: ProtectRouteProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const isAuthPage = location.pathname === '/auth';
  const { setUserProfile } = useProfile();

  if (isAuthPage) {
    localStorage.clear();
  }

  const jwt = queryParams.get('JWT');
  const refreshToken = queryParams.get('REFRESH');
  const [{ data, error, loading }] = useGetUserProfile(jwt || '');
  const [{ data: refreshTokenData, error: refreshError }] = useRefreshToken(
    refreshToken || ''
  );
  const setLinkUrl = process.env.REACT_APP_SET_LINK_URL;
  const [opened, { open }] = useDisclosure();

  const handleTokens = () => {
    if (jwt) localStorage.setItem('accessToken', jwt);
    if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
    if (data) {
      localStorage.setItem('userProfile', JSON.stringify(data));
      setUserProfile(data);
    }
    if (refreshTokenData) {
      localStorage.setItem('accessToken', refreshTokenData.token);
      localStorage.setItem('refreshToken', refreshTokenData.refreshToken);
    }
  };

  const checkUserRole = (roles: string[]) => {
    return roles.some(
      (role) =>
        role === CVUP_ROLE.CVUP_APPROVER || role === CVUP_ROLE.CVUP_CREATOR
    );
  };

  const redirectToSetLink = () => {
    localStorage.clear();
    window.location.href = setLinkUrl || '';
  };

  useEffect(() => {
    if (loading) return;

    if ((error && error.status === 401) || refreshError?.status === 401) {
      localStorage.clear();
      open();
      return;
    }

    if ((error && error.status === 403) || refreshError?.status === 403) {
      redirectToSetLink();
      return;
    }

    if (data) {
      if (!checkUserRole(data.N_ROLES)) {
        redirectToSetLink();
        return;
      }
      handleTokens();
      if (isAuthPage) {
        navigate('/policies');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data,
    error,
    refreshTokenData,
    refreshError,
    loading,
    isAuthPage,
    open,
    navigate,
  ]);

  if (loading) {
    return (
      <Flex justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress size={50} />
      </Flex>
    );
  }

  return (
    <>
      {children}
      <ModalRedirect open={opened} />
    </>
  );
};

export default ProtectRoute;
